import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BaseCard } from '../common/BaseCard';
import { formatDateSafe, formatCurrencySafe } from '../utils/formatters';
import { ScoreGauge } from '../../../../../components/score-gauge/ScoreGauge';
import {
  IAnaliseCreditoPF,
  IAnaliseCreditoPJ,
  IOcorrenciaPendenciaFinanceira,
  IOcorrenciaProtesto,
} from '../../../../../../application/models/dossie';

export const AnaliseCreditoCard = ({
  serasaScore,
}: {
  serasaScore: IAnaliseCreditoPF | IAnaliseCreditoPJ | null;
}) => {
  if (!serasaScore) return null;

  const qtdCheques = Number(serasaScore.chequesemfundos?.quantidadeocorrencia || 0);
  const qtdPendencias = Number(serasaScore.pendenciasfinanceiras?.quantidadeocorrencia || 0);
  const qtdProtestos = Number(serasaScore.protestos?.quantidadeocorrencia || 0);

  const ocorrenciasPendencias: IOcorrenciaPendenciaFinanceira[] =
    serasaScore.pendenciasfinanceiras?.ocorrencias || [];
  const ocorrenciasProtestos: IOcorrenciaProtesto[] =
    serasaScore.protestos?.ocorrencias || [];

  return (
    <BaseCard title="INTELIGÊNCIA QUE APOIA DECISÕES DE CRÉDITO MAIS SEGURAS" icon={<ShowChartRoundedIcon />} canSplit={true}>
      <Box className="analise-credito-wrapper" display="flex" flexDirection="column" gap={4}>
        {/* TOP SECTION: SCORE GAUGE + QUADRO DE AVISOS */}
        <Grid container spacing={3} alignItems="stretch" className="score-avisos-container avoid-break">
          <Grid item xs={12} md={5} lg={4.5} className="coluna-gauge">
            <Box
              className="stat-card-item avoid-break"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2.5,
                backgroundColor: '#faf8fc',
                borderRadius: '16px',
                border: '1px solid #ece7f2',
                height: '100%',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#68457c', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1 }}
              >
              SCORE - CAPACIDADE DE CRÉDITO
              </Typography>
              <ScoreGauge score={serasaScore.score} />
            </Box>
          </Grid>

          <Grid item xs={12} md={7} lg={7.5} className="coluna-avisos">
            <Box
              className="stat-card-item avoid-break"
              sx={{
                p: 3,
                borderRadius: '16px',
                backgroundColor: '#faf8fc',
                border: '1px solid #ece7f2',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#1e293b', fontWeight: 800, mb: 2.5, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block' }}
              >
                Quadro de Avisos & Apontamentos
              </Typography>
              <Grid container spacing={2}>
                {/* Cheques sem Fundos */}
                <Grid item xs={12} sm={4}>
                  <Box
                    className="stat-card-item avoid-break"
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                      border: qtdCheques > 0 ? '1px solid #fecaca' : '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                      Cheques s/ Fundos
                    </Typography>
                    {qtdCheques > 0 ? (
                      <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 800 }}>
                        {qtdCheques} ocorrência{qtdCheques > 1 ? 's' : ''}
                      </Typography>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 700 }}>
                          Nada consta
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Pendências Financeiras */}
                <Grid item xs={12} sm={4}>
                  <Box
                    className="stat-card-item avoid-break"
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                      border: qtdPendencias > 0 ? '1px solid #fecaca' : '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                      Pendências Fin.
                    </Typography>
                    {qtdPendencias > 0 ? (
                      <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 800 }}>
                        {qtdPendencias} ocorrência{qtdPendencias > 1 ? 's' : ''}
                      </Typography>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 700 }}>
                          Nada consta
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Protestos */}
                <Grid item xs={12} sm={4}>
                  <Box
                    className="stat-card-item avoid-break"
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                      border: qtdProtestos > 0 ? '1px solid #fecaca' : '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                      Protestos
                    </Typography>
                    {qtdProtestos > 0 ? (
                      <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 800 }}>
                        {qtdProtestos} ocorrência{qtdProtestos > 1 ? 's' : ''}
                      </Typography>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 700 }}>
                          Nada consta
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* PENDÊNCIAS FINANCEIRAS */}
        <Box mt={1} className="pendencias-section avoid-break">
          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'block' }}
          >
            PENDÊNCIAS FINANCEIRAS - RESUMO
          </Typography>
          <Grid container spacing={2} mb={3} className="pendencias-resumo-container">
            <Grid item xs={12} sm={4}>
              <Box className="stat-card-item avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', p: 2, bgcolor: '#faf8fc' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                  Quantidade de Ocorrências
                </Typography>
                <Typography variant="h6" sx={{ color: '#723989', fontWeight: 800 }}>
                  {serasaScore.pendenciasfinanceiras?.quantidadeocorrencia || '0'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className="stat-card-item avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', p: 2, bgcolor: '#faf8fc' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                  Último Vencimento
                </Typography>
                <Typography variant="h6" sx={{ color: '#723989', fontWeight: 800 }}>
                  {formatDateSafe(serasaScore.pendenciasfinanceiras?.ultimovencimento)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className="stat-card-item avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', p: 2, bgcolor: '#faf8fc' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                  Valor Total
                </Typography>
                <Typography variant="h6" sx={{ color: '#723989', fontWeight: 800 }}>
                  {formatCurrencySafe(serasaScore.pendenciasfinanceiras?.valortotal)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'block' }}
          >
            PENDÊNCIAS FINANCEIRAS - OCORRÊNCIAS
          </Typography>
          <TableContainer component={Paper} elevation={0} className="avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', overflow: 'hidden' }}>
            <Table size="medium">
              <TableHead sx={{ backgroundColor: '#faf8fc' }}>
                <TableRow>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Vencimento</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Credor</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Contrato</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ocorrenciasPendencias.length > 0 ? (
                  ocorrenciasPendencias.map((oc: IOcorrenciaPendenciaFinanceira, idx: number) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { border: 0 }, '&:hover': { backgroundColor: '#fcfbfe' } }}>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                        {formatDateSafe(oc.datavencimento)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{oc.credor}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{oc.contrato}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#723989' }}>{formatCurrencySafe(oc.valor)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#888' }}>
                      Nenhuma ocorrência encontrada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* PROTESTOS */}
        <Box mt={1} className="protestos-section avoid-break">
          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'block' }}
          >
            PROTESTOS - RESUMO
          </Typography>
          <Grid container spacing={2} mb={3} className="protestos-resumo-container">
            <Grid item xs={12} sm={6}>
              <Box className="stat-card-item avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', p: 2, bgcolor: '#faf8fc' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                  Quantidade de Ocorrências
                </Typography>
                <Typography variant="h6" sx={{ color: '#723989', fontWeight: 800 }}>
                  {serasaScore.protestos?.quantidadeocorrencia ?? 0}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className="stat-card-item avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', p: 2, bgcolor: '#faf8fc' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                  Valor Total
                </Typography>
                <Typography variant="h6" sx={{ color: '#723989', fontWeight: 800 }}>
                  {formatCurrencySafe(serasaScore.protestos?.valortotal)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'block' }}
          >
            PROTESTOS - OCORRÊNCIAS
          </Typography>
          <TableContainer component={Paper} elevation={0} className="avoid-break" sx={{ border: '1px solid #ece7f2', borderRadius: '12px', overflow: 'hidden' }}>
            <Table size="medium">
              <TableHead sx={{ backgroundColor: '#faf8fc' }}>
                <TableRow>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Data</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Credor</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Valor</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Cartório</TableCell>
                  <TableCell sx={{ color: '#68457c', fontWeight: 700, fontSize: '0.8rem', py: 1.75 }}>Origem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ocorrenciasProtestos.length > 0 ? (
                  ocorrenciasProtestos.map((oc: IOcorrenciaProtesto, idx: number) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { border: 0 }, '&:hover': { backgroundColor: '#fcfbfe' } }}>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                        {formatDateSafe(oc.data)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{oc.credor}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#723989' }}>{formatCurrencySafe(oc.valor)}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{oc.cartorio}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{oc.origem}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#888' }}>
                      Nenhuma ocorrência encontrada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </BaseCard>
  );
};
